import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { throwIfNotOk } from "@/lib/api-error";
import { QuestionnaireEditor } from "@/components/admin/enrollment-questionnaire/QuestionnaireEditor";
import { CircleNotch, WarningCircle } from "@phosphor-icons/react";
import { formatDateTimeBs } from "@/lib/format";
import type { EnrollmentQuestion } from "@/types/enrollment-questionnaire";

interface QuestionnaireData {
  questions: EnrollmentQuestion[];
  updatedAt: string;
  updatedBy: { id: string; name: string } | null;
}

export function EnrollmentQuestionnaireSection() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "enrollment-questionnaire"],
    queryFn: async () => {
      const res = await fetch("/api/admin/enrollment-questionnaire");
      await throwIfNotOk(res, "Greška pri učitavanju upitnika");
      return (await res.json()) as { data: QuestionnaireData };
    },
  });

  const mutation = useMutation({
    mutationFn: async (questions: { id?: string; prompt: string }[]) => {
      const res = await fetch("/api/admin/enrollment-questionnaire", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ questions }),
      });
      await throwIfNotOk(res, "Greška pri snimanju upitnika");
      return (await res.json()) as { data: QuestionnaireData };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "enrollment-questionnaire"] });
      queryClient.invalidateQueries({ queryKey: ["enrollment-questionnaire"] });
    },
  });

  if (isLoading || !data) {
    return (
      <div className="flex min-h-[30vh] flex-col items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
        <CircleNotch className="size-7 animate-spin text-primary/40" />
        <span className="font-heading italic" style={{ fontFamily: "var(--font-heading)" }}>
          Učitavanje…
        </span>
      </div>
    );
  }

  const updated = data.data.updatedBy ? formatDateTimeBs(data.data.updatedAt) : null;

  return (
    <div className="flex flex-col gap-5">
      {updated && data.data.updatedBy && (
        <p className="text-xs text-muted-foreground">
          Posljednja izmjena: <span className="text-foreground/80">{updated}</span> ·{" "}
          <span className="text-foreground/80">{data.data.updatedBy.name}</span>
        </p>
      )}
      <QuestionnaireEditor
        key={data.data.updatedAt}
        initialQuestions={data.data.questions}
        isSaving={mutation.isPending}
        onSave={(qs) => mutation.mutate(qs)}
      />
      {mutation.isError && (
        <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <WarningCircle className="mt-0.5 size-4 shrink-0" />
          <span>
            {mutation.error instanceof Error
              ? mutation.error.message
              : "Greška pri snimanju."}
          </span>
        </div>
      )}
    </div>
  );
}
